import { useMemo } from 'react';
import { MedicalFormDefinition, MedicalFormField } from '../types/medicalForms';

function toArrayValue(value: unknown) {
  if (Array.isArray(value)) return value.map((item) => String(item));
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

function isVisible(field: MedicalFormField, answers: Record<string, unknown>) {
  if (!field.conditional) return true;
  const left = answers[field.conditional.fieldId];
  const right = field.conditional.value;
  const operator = field.conditional.operator;

  if (operator === 'equals') return String(left ?? '') === String(right ?? '');
  if (operator === 'not_equals') return String(left ?? '') !== String(right ?? '');
  if (operator === 'greater_than') return Number(left) > Number(right);
  if (operator === 'less_than') return Number(left) < Number(right);

  const leftValues = Array.isArray(left) ? left.map((value) => String(value)) : [String(left ?? '')];
  const rightString = String(right ?? '');
  if (operator === 'includes') {
    return leftValues.some((value) => value === rightString);
  }
  if (operator === 'not_includes') {
    return !leftValues.some((value) => value === rightString);
  }
  return true;
}

type MedicalFormRendererProps = {
  definition: MedicalFormDefinition;
  answers: Record<string, unknown>;
  onChange?: (fieldId: string, value: unknown) => void;
  readOnly?: boolean;
};

export function MedicalFormRenderer({ definition, answers, onChange, readOnly = false }: MedicalFormRendererProps) {
  const visibleMap = useMemo(() => {
    const next = new Map<string, boolean>();
    definition.sections.forEach((section) => {
      section.fields.forEach((field) => {
        next.set(field.id, isVisible(field, answers));
      });
    });
    return next;
  }, [definition, answers]);

  function updateField(field: MedicalFormField, value: unknown) {
    if (readOnly) return;
    onChange?.(field.id, value);
  }

  function updateMatrixCell(field: MedicalFormField, rowValue: string, columnValue: string, checkedOrValue: boolean | string) {
    const current = answers[field.id] && typeof answers[field.id] === 'object' && !Array.isArray(answers[field.id])
      ? (answers[field.id] as Record<string, unknown>)
      : {};
    const row = current[rowValue] && typeof current[rowValue] === 'object' && !Array.isArray(current[rowValue])
      ? (current[rowValue] as Record<string, unknown>)
      : {};
    const inputType = field.matrix?.inputType || 'radio';
    const nextRow =
      inputType === 'radio'
        ? { value: columnValue }
        : inputType === 'checkbox'
          ? { ...row, [columnValue]: checkedOrValue }
          : { ...row, [columnValue]: checkedOrValue };
    updateField(field, { ...current, [rowValue]: nextRow });
  }

  return (
    <div className="space-y-4">
      {definition.introText && <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">{definition.introText}</div>}
      {definition.sections.map((section) => (
        <div key={section.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
          <div>
            <p className="text-base font-semibold text-slate-900">{section.title}</p>
            {section.description && <p className="text-sm text-slate-500 mt-1">{section.description}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {section.fields.map((field) => {
              if (!visibleMap.get(field.id)) return null;
              const value = answers[field.id];
              const width = field.layout?.width === 'half' ? 'md:col-span-1' : 'md:col-span-2';
              if (field.type === 'instruction') {
                return (
                  <div key={field.id} className={`${width} rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900`}>
                    <p className="font-medium">{field.label}</p>
                    {field.helpText && <p className="mt-1 text-blue-800">{field.helpText}</p>}
                  </div>
                );
              }

              const commonInputProps = {
                disabled: readOnly,
                required: field.required,
                placeholder: field.placeholder || '',
                className: 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500',
              };

              return (
                <div key={field.id} className={`${width} space-y-1`}>
                  <label className="text-sm font-medium text-slate-700">
                    {field.label}
                    {field.required ? <span className="text-rose-600 ml-1">*</span> : null}
                  </label>
                  {field.helpText && <div className="text-xs text-slate-500">{field.helpText}</div>}

                  {field.type === 'textarea' && (
                    <textarea
                      {...commonInputProps}
                      value={String(value ?? '')}
                      rows={4}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  )}

                  {field.type === 'text' && (
                    <input
                      {...commonInputProps}
                      type="text"
                      value={String(value ?? '')}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  )}

                  {field.type === 'number' && (
                    <input
                      {...commonInputProps}
                      type="number"
                      value={value === null || value === undefined ? '' : String(value)}
                      min={field.validation?.min}
                      max={field.validation?.max}
                      step={field.validation?.step ?? 1}
                      onChange={(event) => updateField(field, event.target.value === '' ? null : Number(event.target.value))}
                    />
                  )}

                  {(field.type === 'date' || field.type === 'datetime' || field.type === 'time' || field.type === 'email' || field.type === 'phone' || field.type === 'url') && (
                    <input
                      {...commonInputProps}
                      type={field.type === 'datetime' ? 'datetime-local' : field.type === 'phone' ? 'tel' : field.type}
                      value={String(value ?? '')}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  )}

                  {field.type === 'select' && (
                    <select
                      {...commonInputProps}
                      value={String(value ?? '')}
                      onChange={(event) => updateField(field, event.target.value)}
                    >
                      <option value="">Select...</option>
                      {(field.options || []).map((option) => (
                        <option key={`${field.id}-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {field.type === 'radio' && (
                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      {(field.options || []).map((option) => (
                        <label key={`${field.id}-${option.value}`} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name={`radio-${field.id}`}
                            disabled={readOnly}
                            checked={String(value ?? '') === option.value}
                            onChange={() => updateField(field, option.value)}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  )}

                  {field.type === 'multiselect' && (
                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      {(field.options || []).map((option) => {
                        const values = toArrayValue(value);
                        const checked = values.includes(option.value);
                        return (
                          <label key={`${field.id}-${option.value}`} className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              disabled={readOnly}
                              checked={checked}
                              onChange={(event) => {
                                const next = new Set(values);
                                if (event.target.checked) next.add(option.value);
                                else next.delete(option.value);
                                updateField(field, Array.from(next));
                              }}
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {field.type === 'checkbox' && (field.options || []).length > 0 && (
                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      {(field.options || []).map((option) => {
                        const values = toArrayValue(value);
                        const checked = values.includes(option.value);
                        return (
                          <label key={`${field.id}-${option.value}`} className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              disabled={readOnly}
                              checked={checked}
                              onChange={(event) => {
                                const next = new Set(values);
                                if (event.target.checked) next.add(option.value);
                                else next.delete(option.value);
                                updateField(field, Array.from(next));
                              }}
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {field.type === 'checkbox' && (field.options || []).length === 0 && (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={Boolean(value)}
                        onChange={(event) => updateField(field, event.target.checked)}
                      />
                      Mark as confirmed
                    </label>
                  )}

                  {field.type === 'switch' && (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={Boolean(value)}
                        onChange={(event) => updateField(field, event.target.checked)}
                      />
                      Enabled
                    </label>
                  )}

                  {(field.type === 'rating' || field.type === 'scale') && (
                    <div className="space-y-1">
                      <input
                        {...commonInputProps}
                        type="range"
                        min={field.validation?.min ?? 0}
                        max={field.validation?.max ?? 10}
                        step={field.validation?.step ?? 1}
                        value={Number(value ?? field.validation?.min ?? 0)}
                        onChange={(event) => updateField(field, Number(event.target.value))}
                      />
                      <div className="text-xs text-slate-500">Value: {String(value ?? field.validation?.min ?? 0)}</div>
                    </div>
                  )}

                  {field.type === 'matrix' && (
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Question</th>
                            {(field.matrix?.columns || []).map((column) => (
                              <th key={`${field.id}-${column.value}`} className="px-3 py-2 text-left font-medium text-slate-600">
                                {column.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {(field.matrix?.rows || []).map((row) => {
                            const matrixValue = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
                            const rowValue = matrixValue[row.value] && typeof matrixValue[row.value] === 'object' ? matrixValue[row.value] : {};
                            return (
                              <tr key={`${field.id}-${row.value}`}>
                                <td className="px-3 py-2 font-medium text-slate-700">{row.label}</td>
                                {(field.matrix?.columns || []).map((column) => {
                                  const inputType = field.matrix?.inputType || 'radio';
                                  return (
                                    <td key={`${field.id}-${row.value}-${column.value}`} className="px-3 py-2">
                                      {inputType === 'text' || inputType === 'paragraph' ? (
                                        inputType === 'paragraph' ? (
                                          <textarea
                                            disabled={readOnly}
                                            rows={2}
                                            className="w-full min-w-[160px] rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
                                            value={String(rowValue[column.value] ?? '')}
                                            onChange={(event) => updateMatrixCell(field, row.value, column.value, event.target.value)}
                                          />
                                        ) : (
                                          <input
                                            disabled={readOnly}
                                            className="w-full min-w-[140px] rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
                                            value={String(rowValue[column.value] ?? '')}
                                            onChange={(event) => updateMatrixCell(field, row.value, column.value, event.target.value)}
                                          />
                                        )
                                      ) : (
                                        <input
                                          type={inputType === 'checkbox' ? 'checkbox' : 'radio'}
                                          name={`matrix-${field.id}-${row.value}`}
                                          disabled={readOnly}
                                          checked={inputType === 'checkbox' ? Boolean(rowValue[column.value]) : rowValue.value === column.value}
                                          onChange={(event) => updateMatrixCell(field, row.value, column.value, event.target.checked)}
                                        />
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {field.type === 'signature' && (
                    <input
                      {...commonInputProps}
                      type="text"
                      placeholder={field.placeholder || 'Type full legal name as signature'}
                      value={String(value ?? '')}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  )}

                  {field.type === 'file' && (
                    <div className="space-y-2">
                      {!readOnly && (
                        <input
                          type="file"
                          multiple
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                          onChange={(event) => {
                            const files = Array.from(event.target.files || []).map((file) => file.name);
                            updateField(field, files);
                          }}
                        />
                      )}
                      <div className="text-xs text-slate-500">
                        {(toArrayValue(value).length ? toArrayValue(value).join(', ') : 'No files selected')}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
